/**
 * Capture a cropped JPEG preview of a PDF comment pin for email notifications.
 */

import { pdfjs } from 'react-pdf';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';
import { portalHostedAssetHref } from './portalAssetUrl';
import { getAvatarBgColor, getUserInitials } from './avatarUtils';

/** Crop region size as a fraction of the rendered page (width × height). */
const CROP_W_FRAC = 0.48;
const CROP_H_FRAC = 0.38;
const PDF_RENDER_SCALE = 2;

export interface CommentPreviewMarker {
  userName: string;
  userId?: string;
  photoURL?: string | null;
}

let workerConfigured = false;

function ensurePdfWorker(): void {
  if (workerConfigured) return;
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  workerConfigured = true;
}

function findPageCanvas(container: HTMLElement): HTMLCanvasElement | null {
  const current = container.querySelector('.pdf-page:not(.pdf-page-prev) canvas');
  if (current instanceof HTMLCanvasElement) return current;
  const any = container.querySelector('canvas');
  return any instanceof HTMLCanvasElement ? any : null;
}

function loadMarkerAvatarImage(photoURL: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = photoURL;
  });
}

function drawInitialsOnAvatar(
  ctx: CanvasRenderingContext2D,
  userName: string,
  cx: number,
  cy: number,
  radius: number,
  userId?: string,
): void {
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = getAvatarBgColor(userId || userName || '?');
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.font = `600 ${Math.max(9, Math.round(radius * 0.82))}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(getUserInitials(userName), cx, cy + 0.5);
}

async function drawCommentPreviewMarker(
  ctx: CanvasRenderingContext2D,
  pinLocalX: number,
  pinLocalY: number,
  cropW: number,
  cropH: number,
  marker?: CommentPreviewMarker,
): Promise<void> {
  const dotRadius = Math.max(5, Math.round(cropW * 0.014));
  const avatarRadius = Math.max(11, Math.round(cropW * 0.03));
  const gap = Math.max(3, Math.round(cropW * 0.008));

  ctx.fillStyle = '#DC2626';
  ctx.beginPath();
  ctx.arc(pinLocalX, pinLocalY, dotRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(1.5, dotRadius * 0.35);
  ctx.stroke();

  if (!marker) return;

  let avatarCx = pinLocalX + dotRadius + gap + avatarRadius;
  let avatarCy = pinLocalY;

  if (avatarCx + avatarRadius > cropW - 2) {
    avatarCx = pinLocalX - dotRadius - gap - avatarRadius;
  }
  if (avatarCy - avatarRadius < 2) {
    avatarCy = avatarRadius + 2;
  } else if (avatarCy + avatarRadius > cropH - 2) {
    avatarCy = cropH - avatarRadius - 2;
  }

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.28)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 1;

  ctx.beginPath();
  ctx.arc(avatarCx, avatarCy, avatarRadius + 1.5, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  const photoUrl = typeof marker.photoURL === 'string' ? marker.photoURL.trim() : '';
  const avatarImg = photoUrl ? await loadMarkerAvatarImage(photoUrl) : null;

  if (avatarImg) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(avatarCx, avatarCy, avatarRadius, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(
      avatarImg,
      avatarCx - avatarRadius,
      avatarCy - avatarRadius,
      avatarRadius * 2,
      avatarRadius * 2,
    );
    ctx.restore();
  } else {
    drawInitialsOnAvatar(ctx, marker.userName, avatarCx, avatarCy, avatarRadius, marker.userId);
  }

  ctx.restore();
}

async function cropCanvasAroundPin(
  source: HTMLCanvasElement,
  pinX: number,
  pinY: number,
  marker?: CommentPreviewMarker,
): Promise<string | undefined> {
  if (source.width < 8 || source.height < 8) return undefined;

  const cx = (pinX / 100) * source.width;
  const cy = (pinY / 100) * source.height;
  const cropW = Math.max(120, Math.round(source.width * CROP_W_FRAC));
  const cropH = Math.max(90, Math.round(source.height * CROP_H_FRAC));

  let sx = Math.round(cx - cropW / 2);
  let sy = Math.round(cy - cropH / 2);
  sx = Math.max(0, Math.min(source.width - cropW, sx));
  sy = Math.max(0, Math.min(source.height - cropH, sy));

  const out = document.createElement('canvas');
  out.width = cropW;
  out.height = cropH;
  const ctx = out.getContext('2d');
  if (!ctx) return undefined;

  ctx.drawImage(source, sx, sy, cropW, cropH, 0, 0, cropW, cropH);

  const pinLocalX = cx - sx;
  const pinLocalY = cy - sy;
  await drawCommentPreviewMarker(ctx, pinLocalX, pinLocalY, cropW, cropH, marker);

  try {
    return out.toDataURL('image/jpeg', 0.84);
  } catch {
    return undefined;
  }
}

/**
 * Draw a cropped region around normalized pin coordinates (0–100) with a pin marker.
 * Uses the on-screen react-pdf canvas when export is allowed.
 */
export async function capturePdfPageContextPreview(
  pageContainer: HTMLElement,
  pinX: number,
  pinY: number,
  marker?: CommentPreviewMarker,
): Promise<string | undefined> {
  const canvas = findPageCanvas(pageContainer);
  if (!canvas) return undefined;
  return cropCanvasAroundPin(canvas, pinX, pinY, marker);
}

/** Re-render the PDF page with pdf.js (avoids tainted on-screen canvas) and crop around the pin. */
export async function capturePdfCommentPreviewFromDocument(
  documentUrl: string,
  pageNumber: number,
  pinX: number,
  pinY: number,
  marker?: CommentPreviewMarker,
): Promise<string | undefined> {
  const fetchUrl = portalHostedAssetHref(documentUrl) || documentUrl;
  if (!fetchUrl.trim()) return undefined;

  try {
    ensurePdfWorker();
    const pdf = await pdfjs.getDocument(fetchUrl).promise;
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    return cropCanvasAroundPin(canvas, pinX, pinY, marker);
  } catch (err) {
    console.warn('[capturePdfCommentPreviewFromDocument] failed', err);
    return undefined;
  }
}

/** Capture a region around a pin on a full-page image viewer. */
export async function captureImagePageContextPreview(
  img: HTMLImageElement,
  pinX: number,
  pinY: number,
  marker?: CommentPreviewMarker,
): Promise<string | undefined> {
  if (!img.naturalWidth || !img.naturalHeight) return undefined;

  const cx = (pinX / 100) * img.naturalWidth;
  const cy = (pinY / 100) * img.naturalHeight;
  const cropW = Math.max(120, Math.round(img.naturalWidth * CROP_W_FRAC));
  const cropH = Math.max(90, Math.round(img.naturalHeight * CROP_H_FRAC));

  let sx = Math.round(cx - cropW / 2);
  let sy = Math.round(cy - cropH / 2);
  sx = Math.max(0, Math.min(img.naturalWidth - cropW, sx));
  sy = Math.max(0, Math.min(img.naturalHeight - cropH, sy));

  const out = document.createElement('canvas');
  out.width = cropW;
  out.height = cropH;
  const ctx = out.getContext('2d');
  if (!ctx) return undefined;

  try {
    ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, cropW, cropH);
  } catch {
    return undefined;
  }

  const pinLocalX = cx - sx;
  const pinLocalY = cy - sy;
  await drawCommentPreviewMarker(ctx, pinLocalX, pinLocalY, cropW, cropH, marker);

  try {
    return out.toDataURL('image/jpeg', 0.84);
  } catch {
    return undefined;
  }
}

export async function uploadPdfCommentContextPreview(
  dataUrl: string,
  documentId: string,
  projectId?: string,
): Promise<string> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const safeDoc = (documentId || 'doc').replace(/[^\w.-]+/g, '_').slice(0, 80);
  const safeProj = (projectId || 'lead').replace(/[^\w.-]+/g, '_').slice(0, 80);
  const path = `pdf-comment-previews/${safeProj}/${safeDoc}/${Date.now()}.jpg`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
  return getDownloadURL(storageRef);
}
